/**
 * Heat Meter Utility
 *
 * Calculates deal quality indicator based on:
 * - Value score: Entry price vs category average (lower = better deal)
 * - Interest score: Entry velocity + sell percentage
 * - Time: New (<24h) = Fresh, ending (<24h) = Last Chance
 *
 * Tiers:
 * - Hot Deal: Great value + high interest
 * - Heating Up: Good value or growing interest
 * - Fresh: New listing
 * - Last Chance: Ending soon
 */

// Category average entry prices (in cents)
// Used to calculate value score
const CATEGORY_AVERAGES = {
  electronics: 1500,      // €15
  gaming: 1200,           // €12
  fashion: 800,           // €8
  home: 1000,             // €10
  sports: 900,            // €9
  vehicles: 2500,         // €25
  collectibles: 1500,     // €15
  toys: 600,              // €6
  music: 700,             // €7
  books: 400,             // €4
  other: 1000,            // €10 default
};

/**
 * Heat tier definitions
 */
export const HEAT_TIERS = {
  HOT_DEAL: {
    id: 'hot-deal',
    label: 'Hot Deal',
    icon: '🔥',
    iconCount: 3,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/30',
  },
  HEATING_UP: {
    id: 'heating-up',
    label: 'Heating Up',
    icon: '⚡',
    iconCount: 1,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/20',
    borderColor: 'border-yellow-400/30',
  },
  FRESH: {
    id: 'fresh',
    label: 'Fresh',
    icon: '✨',
    iconCount: 1,
    color: 'text-primary',
    bgColor: 'bg-primary/20',
    borderColor: 'border-primary/30',
  },
  LAST_CHANCE: {
    id: 'last-chance',
    label: 'Last Chance',
    icon: '⏰',
    iconCount: 1,
    color: 'text-error',
    bgColor: 'bg-error/20',
    borderColor: 'border-error/30',
  },
  NORMAL: {
    id: 'normal',
    label: null,
    icon: null,
    iconCount: 0,
    color: '',
    bgColor: '',
    borderColor: '',
  },
};

/**
 * Calculate the heat tier for a raffle
 *
 * @param {Object} raffle - The raffle object
 * @param {string} raffle.category - Raffle category
 * @param {number} raffle.ticketPrice - Entry price in cents
 * @param {number} raffle.ticketQuantity - Total entries available
 * @param {number} raffle.ticketsSold - Entries sold so far
 * @param {Date|Object} raffle.createdAt - Creation timestamp
 * @param {Date|Object} raffle.endsAt - End timestamp
 *
 * @returns {Object} Heat tier configuration
 */
export function calculateHeat(raffle) {
  if (!raffle) return HEAT_TIERS.NORMAL;

  const now = Date.now();

  // Parse timestamps
  const createdAt = raffle.createdAt?.toDate?.()
    ? raffle.createdAt.toDate().getTime()
    : raffle.createdAt instanceof Date
    ? raffle.createdAt.getTime()
    : new Date(raffle.createdAt).getTime();

  const endsAt = raffle.endsAt?.toDate?.()
    ? raffle.endsAt.toDate().getTime()
    : raffle.endsAt instanceof Date
    ? raffle.endsAt.getTime()
    : new Date(raffle.endsAt).getTime();

  const hoursOld = (now - createdAt) / (1000 * 60 * 60);
  const hoursRemaining = (endsAt - now) / (1000 * 60 * 60);

  // Time-based checks (highest priority)
  // Last Chance: Ending within 24 hours
  if (hoursRemaining > 0 && hoursRemaining < 24) {
    return HEAT_TIERS.LAST_CHANCE;
  }

  // Fresh: Listed within last 24 hours
  if (hoursOld < 24) {
    return HEAT_TIERS.FRESH;
  }

  // Calculate value score (lower price = better deal)
  const categoryAvg = CATEGORY_AVERAGES[raffle.category] || CATEGORY_AVERAGES.other;
  const priceDiff = (categoryAvg - raffle.ticketPrice) / categoryAvg;
  const isGoodValue = priceDiff > 0.15; // 15%+ below average

  // Calculate interest score
  const sellPercentage = raffle.ticketsSold / raffle.ticketQuantity;
  const totalDuration = (endsAt - createdAt) / (1000 * 60 * 60);
  const elapsedDuration = (now - createdAt) / (1000 * 60 * 60);
  const expectedSellRate = elapsedDuration / totalDuration;
  const actualVsExpected = sellPercentage / Math.max(expectedSellRate, 0.1);
  const isHighInterest = actualVsExpected > 1.3 || sellPercentage > 0.5;

  // Determine tier
  // Hot Deal: Good value AND high interest
  if (isGoodValue && isHighInterest) {
    return HEAT_TIERS.HOT_DEAL;
  }

  // Heating Up: Either good value OR high interest (but not both)
  if (isGoodValue || isHighInterest) {
    return HEAT_TIERS.HEATING_UP;
  }

  // Normal (no special indicator)
  return HEAT_TIERS.NORMAL;
}

/**
 * Get the heat icon string with proper count
 */
export function getHeatIcon(tier) {
  if (!tier || !tier.icon) return '';
  return tier.icon.repeat(tier.iconCount);
}

export default { calculateHeat, getHeatIcon, HEAT_TIERS, CATEGORY_AVERAGES };
